import type { RemoteClient } from "./remote-client"
import { normalizeDataDependencies, type DataDependency, type DataDependencyInput } from "../data/data-invalidation"
import type {
  AnyGraphQLDocument,
  GraphQLDocument,
  GraphQLDocumentData,
  GraphQLDocumentVariables,
  NoGraphQLVariables,
  GraphQLResultMapping,
} from "./graphql-document"

export interface GraphQLQueryExecutor {
  query<TData, TVariables = Record<string, unknown>>(
    endpoint: string,
    query: string,
    variables?: TVariables,
    signal?: AbortSignal
  ): Promise<TData>
}

export type GraphQLQueryExecutionTarget = RemoteClient | GraphQLQueryExecutor

export interface GraphQLQueryExecutionOptions {
  signal?: AbortSignal
}

/** The arguments of `create`: none for a query without parameters, optional when every parameter is optional. */
type QueryCreateArguments<TParameters> = [TParameters] extends [void]
  ? []
  : Record<never, never> extends TParameters
    ? [parameters?: TParameters]
    : [parameters: TParameters]

/**
 * The arguments of `execute` after the target: the parameters when the query takes any (optional when every
 * parameter is optional), then the options.
 */
type QueryExecuteArguments<TParameters> = [TParameters] extends [void]
  ? [options?: GraphQLQueryExecutionOptions]
  : Record<never, never> extends TParameters
    ? [parameters?: TParameters, options?: GraphQLQueryExecutionOptions]
    : [parameters: TParameters, options?: GraphQLQueryExecutionOptions]

/** An operation that declares variables, such as `query Customer($id: Int!) { … }`. */
const declaresVariables = /^\s*query\b[^{]*\(/

export interface TypedGraphQLQueryDefinitionOptions<TDocument extends AnyGraphQLDocument> {
  endpoint: string
  document: TDocument
  codec?: GraphQLQueryCodec
  consistency?: GraphQLQueryConsistency<DocumentQueryParameters<GraphQLDocumentVariables<TDocument>>>
}

/** Generated enum conversions and, for feature Root queries, response extraction. */
export interface GraphQLQueryCodec {
  readonly result: GraphQLResultMapping
  encodeVariables(value: object): object
  decodeVariables(value: object): object
}

export interface MappedGraphQLQueryDefinitionOptions<TDocument extends AnyGraphQLDocument, TParameters> extends Omit<
  TypedGraphQLQueryDefinitionOptions<TDocument>,
  "consistency"
> {
  variables: (parameters: Readonly<TParameters>) => GraphQLDocumentVariables<TDocument>
  consistency?: GraphQLQueryConsistency<TParameters>
}

type DocumentQueryParameters<TVariables extends object> = keyof TVariables extends never ? void : TVariables

export interface GraphQLQueryConsistency<TParameters> {
  readonly dependsOn: DataDependencyInput | ((parameters: Readonly<TParameters>) => DataDependencyInput)
}

function resolveGraphQLExecutor(target: GraphQLQueryExecutionTarget): GraphQLQueryExecutor {
  return "graphql" in target ? target.graphql : target
}

export type DeepReadonly<T> = T extends readonly (infer TItem)[]
  ? readonly DeepReadonly<TItem>[]
  : T extends object
    ? { readonly [TKey in keyof T]: DeepReadonly<T[TKey]> }
    : T

function deepFreeze<T>(value: T, seen = new WeakSet<object>()): DeepReadonly<T> {
  if (value == null || typeof value !== "object") return value as DeepReadonly<T>
  if (seen.has(value)) return value as DeepReadonly<T>
  seen.add(value)

  for (const child of Object.values(value)) {
    deepFreeze(child, seen)
  }

  return Object.freeze(value) as DeepReadonly<T>
}

function snapshotVariables<TVariables extends object>(variables: TVariables): DeepReadonly<TVariables> {
  return deepFreeze(structuredClone(variables))
}

export class GraphQLQueryInstance<TData, TVariables extends object = NoGraphQLVariables> {
  readonly endpoint: string
  readonly document: GraphQLDocument<TData, TVariables>
  readonly variables?: DeepReadonly<TVariables>

  constructor(
    endpoint: string,
    document: GraphQLDocument<TData, TVariables>,
    variables?: TVariables,
    private readonly codec?: GraphQLQueryCodec
  ) {
    this.endpoint = endpoint
    this.document = document
    this.variables =
      variables == null ? undefined : snapshotVariables((codec?.encodeVariables(variables) ?? variables) as TVariables)
  }

  async execute(target: GraphQLQueryExecutionTarget, options: GraphQLQueryExecutionOptions = {}) {
    const data = await resolveGraphQLExecutor(target).query<TData, Readonly<TVariables>>(
      this.endpoint,
      this.document,
      this.variables as Readonly<TVariables> | undefined,
      options.signal
    )
    return (this.codec === undefined ? data : this.codec.result.decode(data)) as TData
  }
}

export class GraphQLQueryDefinition<TData, TParameters = void, TVariables extends object = NoGraphQLVariables> {
  private readonly variablesBuilder?: (parameters: TParameters) => TVariables
  private readonly consistency?: GraphQLQueryConsistency<TParameters>

  /** Whether the query takes parameters: its document declares variables. A variables builder only maps to those. */
  private readonly parameterized: boolean

  readonly endpoint: string
  readonly document: GraphQLDocument<TData, TVariables>

  constructor(
    endpoint: string,
    document: GraphQLDocument<TData, TVariables>,
    variablesBuilder?: (parameters: TParameters) => TVariables,
    consistency?: GraphQLQueryConsistency<TParameters>,
    readonly codec?: GraphQLQueryCodec
  ) {
    this.endpoint = endpoint
    this.document = document
    this.variablesBuilder = variablesBuilder
    this.consistency = consistency
    this.parameterized = declaresVariables.test(document)
  }

  dependencies(parameters: TParameters): readonly DataDependency[] {
    const dependsOn = this.consistency?.dependsOn
    if (dependsOn == null) return []
    return normalizeDataDependencies(
      typeof dependsOn === "function" ? dependsOn(this.withDefaultParameters(parameters)) : dependsOn
    )
  }

  /** Omitted parameters of a query whose parameters are all optional are an empty object. */
  private withDefaultParameters(parameters: TParameters | undefined) {
    return (parameters === undefined && this.parameterized ? {} : parameters) as Readonly<TParameters>
  }

  create(...args: QueryCreateArguments<TParameters>) {
    const parameters = this.withDefaultParameters(args[0]) as TParameters
    const variables =
      this.variablesBuilder != null
        ? this.variablesBuilder(parameters)
        : parameters !== undefined
          ? (parameters as unknown as TVariables)
          : undefined
    return new GraphQLQueryInstance<TData, TVariables>(this.endpoint, this.document, variables, this.codec)
  }

  /**
   * `create(parameters).execute(target, options)`, or `create().execute(target, options)` for a query without
   * parameters. When every parameter is optional, `execute(target)` and `execute(target, undefined, options)` omit them.
   */
  execute(target: GraphQLQueryExecutionTarget, ...args: QueryExecuteArguments<TParameters>): Promise<TData> {
    const create = this.create.bind(this) as (parameters?: TParameters) => GraphQLQueryInstance<TData, TVariables>
    if (!this.parameterized) return create().execute(target, args[0] as GraphQLQueryExecutionOptions | undefined)
    const [parameters, options] = args as [TParameters, GraphQLQueryExecutionOptions | undefined]
    return create(parameters).execute(target, options)
  }
}

export function defineGraphQLQuery<TDocument extends AnyGraphQLDocument>(
  options: TypedGraphQLQueryDefinitionOptions<TDocument>
): GraphQLQueryDefinition<
  GraphQLDocumentData<TDocument>,
  DocumentQueryParameters<GraphQLDocumentVariables<TDocument>>,
  GraphQLDocumentVariables<TDocument>
>
export function defineGraphQLQuery<TDocument extends AnyGraphQLDocument, TParameters>(
  options: MappedGraphQLQueryDefinitionOptions<TDocument, TParameters>
): GraphQLQueryDefinition<GraphQLDocumentData<TDocument>, TParameters, GraphQLDocumentVariables<TDocument>>
export function defineGraphQLQuery(
  options:
    | TypedGraphQLQueryDefinitionOptions<AnyGraphQLDocument>
    | MappedGraphQLQueryDefinitionOptions<AnyGraphQLDocument, unknown>
) {
  return new GraphQLQueryDefinition(
    options.endpoint,
    options.document,
    "variables" in options ? (options.variables as (parameters: unknown) => object) : undefined,
    options.consistency as GraphQLQueryConsistency<unknown> | undefined,
    options.codec
  )
}

export { resolveGraphQLExecutor }
